import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Plus, Search, MoreHorizontal, Trash2, Loader2,
  CheckCircle2, XCircle, Clock, RotateCw,
  ArrowRightLeft, PlayCircle, SkipForward, Eye,
} from 'lucide-react'
import { getAllJobs, deleteJob, startMigration, getProgress } from '@/services/api'
import { cn } from '@/lib/utils'

export default function MigrationsList() {
  const navigate = useNavigate()
  const [jobs,              setJobs]              = useState([])
  const [loading,           setLoading]           = useState(true)
  const [searchQuery,       setSearchQuery]       = useState('')
  const [isDeleteDialogOpen,setIsDeleteDialogOpen]= useState(false)
  const [selectedJob,       setSelectedJob]       = useState(null)
  const [deleting,          setDeleting]          = useState(false)
  const [starting,          setStarting]          = useState(null)

  useEffect(() => {
    loadJobs()
    const interval = setInterval(loadJobs, 5000)
    return () => clearInterval(interval)
  }, [])

  const loadJobs = async () => {
    try { const data = await getAllJobs(); setJobs(data) }
    catch (err) { console.error('Error loading jobs:', err) }
    finally     { setLoading(false) }
  }

  const handleDelete = async () => {
    if (!selectedJob) return
    setDeleting(true)
    try {
      await deleteJob(selectedJob.jobId)
      setIsDeleteDialogOpen(false)
      setSelectedJob(null)
      loadJobs()
    } catch (err) { console.error('Error deleting job:', err) }
    setDeleting(false)
  }

  const handleStart = async (job) => {
    setStarting(job.jobId)
    try {
      const progress = await getProgress(job.jobId)
      await startMigration(job.jobId, progress.config)
      navigate(`/dashboard/migrations/${job.jobId}/migrate`)
    } catch (err) { console.error('Error starting job:', err) }
    setStarting(null)
  }

  const openDeleteDialog = (job) => { setSelectedJob(job); setIsDeleteDialogOpen(true) }
  const handleResume     = (job) => navigate(`/dashboard/migrations/${job.jobId}/migrate`)
  const handleView       = (job) => navigate(`/dashboard/migrations/${job.jobId}/migrate`)

  const getStatusConfig = (status) => {
    switch (status) {
      case 'COMPLETED': return { label:'Completed', Icon:CheckCircle2, cls:'chip-success border-0' }
      case 'FAILED':    return { label:'Failed',    Icon:XCircle,      cls:'bg-destructive/10 text-destructive border-0' }
      case 'RUNNING':   return { label:'Running',   Icon:RotateCw,     cls:'chip-amber border-0', spin:true }
      case 'CONFIGURED':return { label:'Ready',     Icon:Clock,        cls:'chip-warning border-0' }
      default:          return { label:status||'Draft', Icon:Clock,    cls:'bg-black/5 text-foreground/50 border-0' }
    }
  }

  const filteredJobs = jobs.filter(job =>
    job.jobName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    job.jobId.toLowerCase().includes(searchQuery.toLowerCase())
  )

  return (
    <div className="space-y-6">

      {/* ── Header ── */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold tracking-tight text-foreground/90">Migrations</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Manage and monitor email migration jobs
          </p>
        </div>
        <Button onClick={() => navigate('/dashboard/migrations/new')} size="sm" className="shrink-0">
          <Plus className="w-3.5 h-3.5 mr-1.5" />
          New Migration
        </Button>
      </div>

      {/* ── Search ── */}
      <div className="relative max-w-xs">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground/60" />
        <Input
          placeholder="Search migrations…"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-9 h-8 text-sm"
        />
      </div>

      {/* ── List ── */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-5 h-5 animate-spin text-primary/50" />
        </div>
      ) : filteredJobs.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-24 text-center">
          <div className="w-14 h-14 rounded-2xl soft-card flex items-center justify-center mb-4 !transform-none">
            <ArrowRightLeft className="w-6 h-6 text-primary/50" />
          </div>
          <p className="text-sm font-medium text-foreground/70">
            {searchQuery ? 'No migrations match your search' : 'No migrations yet'}
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            {!searchQuery && 'Create one to get started.'}
          </p>
          {!searchQuery && (
            <Button
              variant="outline"
              size="sm"
              className="mt-4"
              onClick={() => navigate('/dashboard/migrations/new')}
            >
              <Plus className="w-3.5 h-3.5 mr-1.5" />
              New Migration
            </Button>
          )}
        </div>
      ) : (
        <div className="soft-card rounded-2xl overflow-hidden !transform-none">
          {/* Column headers */}
          <div className="grid grid-cols-[1fr_110px_148px_80px_40px] gap-4 px-5 py-2.5 border-b border-white/40 bg-black/[0.02]">
            {['Job', 'Status', 'Progress', 'Accounts', ''].map((h, i) => (
              <span key={i} className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider">
                {h}
              </span>
            ))}
          </div>

          {/* Rows */}
          <div className="divide-y divide-white/30">
            {filteredJobs.map((job) => {
              const { label, Icon, cls, spin } = getStatusConfig(job.status)
              const isFailed    = job.status === 'FAILED'
              const isCompleted = job.status === 'COMPLETED'
              const isRunning   = job.status === 'RUNNING'
              const canResume   = isCompleted || isFailed || isRunning
              const canStart    = job.status === 'CONFIGURED'
              const pct         = job.progressPercentage ?? 0

              return (
                <div
                  key={job.jobId}
                  className="
                    grid grid-cols-[1fr_110px_148px_80px_40px] gap-4
                    px-5 py-3.5 items-center
                    hover:bg-white/30 transition-colors duration-100 group
                  "
                >
                  {/* Job name */}
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-foreground/85 truncate">
                      {job.jobName || 'Untitled Migration'}
                    </p>
                    <p className="text-[11px] text-muted-foreground mt-0.5 font-mono">
                      {job.jobId.slice(0, 8)}
                      {job.createdAt && (
                        <span className="ml-2 font-sans">
                          {new Date(job.createdAt).toLocaleDateString('en-US', { month:'short', day:'numeric' })}
                        </span>
                      )}
                    </p>
                  </div>

                  {/* Status chip */}
                  <div>
                    <span className={cn(
                      'inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-medium',
                      cls
                    )}>
                      <Icon className={cn('w-3 h-3', spin && 'animate-spin')} />
                      {label}
                    </span>
                  </div>

                  {/* Progress bar */}
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-1.5 bg-black/8 rounded-full overflow-hidden">
                      <div
                        className={cn(
                          'h-full rounded-full transition-all duration-700',
                          isCompleted ? 'bg-success' : isFailed ? 'bg-destructive' : 'bg-primary'
                        )}
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                    <span className="text-[11px] text-muted-foreground tabular-nums w-7 text-right">
                      {pct}%
                    </span>
                  </div>

                  {/* Accounts count */}
                  <div className="text-sm text-muted-foreground tabular-nums">
                    {job.totalAccounts ?? '—'}
                  </div>

                  {/* Row actions */}
                  <div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <MoreHorizontal className="w-3.5 h-3.5" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-44">
                        {canStart && (
                          <DropdownMenuItem
                            onClick={() => handleStart(job)}
                            disabled={starting === job.jobId}
                            className="text-[13px]"
                          >
                            {starting === job.jobId
                              ? <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" />
                              : <PlayCircle className="w-3.5 h-3.5 mr-2" />}
                            Start
                          </DropdownMenuItem>
                        )}
                        {canResume && (
                          <DropdownMenuItem
                            onClick={() => handleResume(job)}
                            className="text-[13px]"
                          >
                            <SkipForward className="w-3.5 h-3.5 mr-2" />
                            {isFailed ? 'Resume / Retry' : 'View / Resume'}
                          </DropdownMenuItem>
                        )}
                        {!(canStart || canResume) && (
                          <DropdownMenuItem onClick={() => handleView(job)} className="text-[13px]">
                            <Eye className="w-3.5 h-3.5 mr-2" />
                            View
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={() => openDeleteDialog(job)}
                          className="text-destructive focus:text-destructive text-[13px]"
                        >
                          <Trash2 className="w-3.5 h-3.5 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* ── Delete dialog ── */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Delete migration?</DialogTitle>
            <DialogDescription>
              <strong>{selectedJob?.jobName || 'This migration'}</strong> will be permanently removed.
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" size="sm" onClick={() => setIsDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" size="sm" onClick={handleDelete} disabled={deleting}>
              {deleting && <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" />}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
